import { Helmet } from 'react-helmet';
import { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import Select from '../../components/ui/select';
import { toast } from 'sonner';
import { Phone, RefreshCw, ChevronRight, User, Clock } from 'lucide-react';
import { getLeads, updateLeadStatus } from '../../lib/apiService';

const STATUS_FLOW = ['pending', 'contacted', 'follow-up', 'closed'];

const STATUS_STYLES = {
  pending: 'bg-blue-50 text-blue-700 border border-blue-300',
  contacted: 'bg-yellow-50 text-yellow-700 border border-yellow-300',
  'follow-up': 'bg-violet-50 text-violet-700 border border-violet-300',
  closed: 'bg-emerald-50 text-emerald-700 border border-emerald-300',
};

const STATUS_LABELS = {
  pending: '🕐 Pending',
  contacted: '📞 Contacted',
  'follow-up': '🔄 Follow-Up',
  closed: '✅ Closed',
};

const LeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [updatingId, setUpdatingId] = useState(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLeads({ status: filterStatus });
      setLeads(res.leads || []);
      setTotal(res.total || 0);
      if (res.grand_total !== undefined) setGrandTotal(res.grand_total);
      if (res.counts) setCounts(res.counts);
    } catch (err) {
      toast.error(err.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleStatusChange = async (lead, newStatus) => {
    if (!newStatus || newStatus === lead.status) return;
    setUpdatingId(lead.id);
    try {
      const res = await updateLeadStatus(lead.id, newStatus);
      if (res && res.status === false) {
        throw new Error(res.message || 'Update failed');
      }
      toast.success(`Lead status updated to ${STATUS_LABELS[newStatus] || newStatus}`);
      fetchLeads();
    } catch (err) {
      toast.error(err.message || 'Update failed');
    } finally {
      setUpdatingId(null);
    }
  };

  const getNextStatus = (current) => {
    const idx = STATUS_FLOW.indexOf(current);
    if (idx === -1 || idx === STATUS_FLOW.length - 1) return null;
    return STATUS_FLOW[idx + 1];
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Group by status
  const grouped = STATUS_FLOW.reduce((acc, st) => {
    acc[st] = leads.filter(l => l.status === st);
    return acc;
  }, {});

  return (
    <div className="p-6 lg:p-8">
      <Helmet>
        <title>Leads | JoldiPabo Admin</title>
      </Helmet>
      <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Callback Requests
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">{grandTotal || total} total requests from app users</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchLeads} className="gap-1.5">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-5 flex-wrap">
        <button
          onClick={() => setFilterStatus('')}
          className={`h-9 px-4 rounded-lg text-sm font-medium border transition-all ${filterStatus === '' ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
        >
          All ({grandTotal || total})
        </button>
        {STATUS_FLOW.map(st => (
          <button
            key={st}
            onClick={() => setFilterStatus(st)}
            className={`h-9 px-4 rounded-lg text-sm font-medium border transition-all ${filterStatus === st ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
          >
            {STATUS_LABELS[st]} {counts[st] !== undefined ? `(${counts[st]})` : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-4 border-[#ee7923] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-20 text-slate-400">
          <Phone className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">No callback requests</p>
          <p className="text-sm mt-1">Requests from app users will appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {leads.map(lead => {
            const nextStatus = getNextStatus(lead.status);
            return (
              <Card key={lead.id} className="p-4 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Requester */}
                      <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                        <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                          <User className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-0.5">Requested By</p>
                          <p className="text-sm font-semibold text-slate-900">{lead.requester_name || `User #${lead.requester_id}`}</p>
                          {lead.requester_phone ? (
                            <a href={`tel:${lead.requester_phone}`} className="flex items-center gap-1.5 text-sm text-[#ee7923] hover:underline mt-1 font-medium">
                              <Phone className="w-3.5 h-3.5" /> {lead.requester_phone}
                            </a>
                          ) : (
                            <p className="text-sm text-slate-400 mt-1 italic">No phone number</p>
                          )}
                        </div>
                      </div>

                      {/* Receiver */}
                      <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-xl border border-orange-100">
                        <div className="w-10 h-10 rounded-full bg-orange-100 text-[#ee7923] flex items-center justify-center shrink-0">
                          <Phone className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-orange-600/70 uppercase tracking-wider mb-0.5">Requested To</p>
                          <p className="text-sm font-semibold text-slate-900">{lead.caller_name || `User #${lead.caller_id}`}</p>
                          {lead.caller_phone ? (
                            <a href={`tel:${lead.caller_phone}`} className="flex items-center gap-1.5 text-sm text-[#ee7923] hover:underline mt-1 font-medium">
                              <Phone className="w-3.5 h-3.5" /> {lead.caller_phone}
                            </a>
                          ) : (
                            <p className="text-sm text-slate-400 mt-1 italic">No phone number</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:items-end gap-2 shrink-0">
                    {/* Quick status buttons */}
                    <div className="flex gap-2 items-center flex-wrap sm:flex-nowrap">
                      <div className="w-40">
                        <Select
                          size="sm"
                          isClearable={false}
                          isSearchable={false}
                          options={STATUS_FLOW.map(st => ({ value: st, label: STATUS_LABELS[st] || st }))}
                          value={lead.status}
                          isDisabled={updatingId === lead.id}
                          onChange={v => handleStatusChange(lead, v)}
                        />
                      </div>
                      {nextStatus && (
                        <Button
                          size="sm"
                          onClick={() => handleStatusChange(lead, nextStatus)}
                          disabled={updatingId === lead.id}
                          className="bg-slate-900 hover:bg-slate-800 text-white h-9 gap-1 whitespace-nowrap"
                        >
                          {updatingId === lead.id ? (
                            <span className="flex items-center gap-1">
                              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Updating...
                            </span>
                          ) : (
                            <>Move to: {STATUS_LABELS[nextStatus]} <ChevronRight className="w-3.5 h-3.5" /></>
                          )}
                        </Button>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-wrap sm:justify-end mt-1">
                      <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${STATUS_STYLES[lead.status] || STATUS_STYLES.pending}`}>
                        {STATUS_LABELS[lead.status] || lead.status}
                      </span>
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatDate(lead.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LeadsPage;
